import React, { useEffect, useMemo, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const emptyLine = { item_id: '', requested_qty: '', remarks: '' };

function FoodPurchaseRequisition({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [settingsMode, setSettingsMode] = useState('MANDATORY');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState({
    store_id: '',
    remarks: '',
    items: [{ ...emptyLine }]
  });

  const decodedPermissions = useMemo(() => {
    try {
      const payload = String(token || '').split('.')[1];
      if (!payload) return {};
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const parsed = JSON.parse(atob(padded));
      return parsed?.permissions || {};
    } catch {
      return {};
    }
  }, [token]);

  const canManageAll = Boolean(decodedPermissions.hotel_admin) || Boolean(decodedPermissions.food_admin);
  const canCreatePR = canManageAll || Boolean(decodedPermissions.food_purchase_staff);
  const canApprovePR = canManageAll || Boolean(decodedPermissions.food_purchase_manager);

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  const parseResponse = async (res) => {
    const raw = await res.text();
    try {
      const json = raw ? JSON.parse(raw) : {};
      return { data: json, message: json?.error || '' };
    } catch {
      return { data: {}, message: raw || '' };
    }
  };

  const statusColor = (status) => {
    if (status === 'APPROVED') return '#2f855a';
    if (status === 'PENDING_APPROVAL') return '#d69e2e';
    if (status === 'REJECTED') return '#c53030';
    return '#4a5568';
  };

  const loadBase = async () => {
    if (!chainId || !propertyId) return;
    try {
      const [prRes, storeRes, itemRes, settingsRes] = await Promise.all([
        fetch('/api/v1/food/pr', { headers: headers() }),
        fetch('/api/v1/food/masters/stores', { headers: headers() }),
        fetch('/api/v1/food/masters/items', { headers: headers() }),
        fetch('/api/v1/food/settings/general', { headers: headers() })
      ]);
      const prData = await parseResponse(prRes);
      const storeData = await parseResponse(storeRes);
      const itemData = await parseResponse(itemRes);
      const settingsData = await parseResponse(settingsRes);

      if (!prRes.ok) return setError(prData.message || 'Failed to load PR list');
      if (!storeRes.ok) return setError(storeData.message || 'Failed to load stores');
      if (!itemRes.ok) return setError(itemData.message || 'Failed to load items');
      if (!settingsRes.ok) return setError(settingsData.message || 'Failed to load settings');

      setRows(Array.isArray(prData.data) ? prData.data : []);
      setStores(Array.isArray(storeData.data) ? storeData.data : []);
      setItems(Array.isArray(itemData.data) ? itemData.data : []);
      setSettingsMode(String(settingsData.data?.pr_approval_mode || 'MANDATORY').toUpperCase());
    } catch {
      setError('Network error while loading requisitions');
    }
  };

  useEffect(() => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    loadBase();
  }, [token, chainId, propertyId]);

  const itemOptions = useMemo(
    () => items.map((i) => ({ value: i.id, label: `${i.item_code || ''} - ${i.item_name || ''}` })),
    [items]
  );
  const itemById = useMemo(() => {
    const map = new Map();
    items.forEach((i) => map.set(String(i.id), i));
    return map;
  }, [items]);

  const resetForm = () => {
    setEditingId('');
    setForm({ store_id: '', remarks: '', items: [{ ...emptyLine }] });
  };

  const addLine = () => setForm((prev) => ({ ...prev, items: [...prev.items, { ...emptyLine }] }));
  const removeLine = (idx) => setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) || [{ ...emptyLine }] }));
  const setLine = (idx, patch) => {
    setForm((prev) => ({ ...prev, items: prev.items.map((line, i) => (i === idx ? { ...line, ...patch } : line)) }));
  };

  const validate = () => {
    if (!chainId || !propertyId) return 'Select a property first from Hotel Config';
    if (!String(form.store_id || '').trim()) return 'Store is required';
    const validItems = form.items.filter((l) => String(l.item_id || '').trim() && Number(l.requested_qty || 0) > 0);
    if (!validItems.length) return 'Add at least one item with valid quantity';
    return '';
  };

  const saveDraft = async () => {
    setError('');
    setMessage('');
    if (!canCreatePR) return setError('Only Purchase Staff can create or edit PR');
    const err = validate();
    if (err) return setError(err);
    const payload = {
      store_id: form.store_id,
      remarks: form.remarks || null,
      items: form.items
        .filter((l) => String(l.item_id || '').trim() && Number(l.requested_qty || 0) > 0)
        .map((l) => ({ item_id: l.item_id, requested_qty: Number(l.requested_qty), remarks: l.remarks || null }))
    };
    try {
      const res = await fetch(editingId ? `/api/v1/food/pr/${editingId}` : '/api/v1/food/pr', {
        method: editingId ? 'PUT' : 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const { message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Save failed');
      setMessage(editingId ? 'Draft updated' : 'Draft created');
      resetForm();
      loadBase();
    } catch {
      setError('Network error while saving draft');
    }
  };

  const submitReq = async (id) => {
    setError('');
    setMessage('');
    if (!canCreatePR) return setError('Only Purchase Staff can submit PR');
    try {
      const res = await fetch(`/api/v1/food/pr/${id}/submit`, { method: 'POST', headers: headers() });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Submit failed');
      setMessage(`PR ${data.requisition_no || ''} submitted. Status: ${data.status}`);
      loadBase();
    } catch {
      setError('Network error while submitting requisition');
    }
  };

  const approveReq = async (id) => {
    setError('');
    setMessage('');
    if (!canApprovePR) return setError('Only Purchase Manager can approve PR');
    try {
      const res = await fetch(`/api/v1/food/pr/${id}/approve`, { method: 'POST', headers: headers() });
      const { message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Approve failed');
      setMessage('Requisition approved');
      loadBase();
    } catch {
      setError('Network error while approving requisition');
    }
  };

  const rejectReq = async (id) => {
    if (!canApprovePR) return setError('Only Purchase Manager can reject PR');
    const reason = window.prompt('Enter rejection reason (optional):', '') || '';
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/food/pr/${id}/reject`, {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ reason })
      });
      const { message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Reject failed');
      setMessage('Requisition rejected');
      loadBase();
    } catch {
      setError('Network error while rejecting requisition');
    }
  };

  const loadForEdit = async (id) => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/food/pr/${id}`, { headers: headers() });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Failed to load requisition');
      setEditingId(data.id);
      setForm({
        store_id: data.store_id || '',
        remarks: data.remarks || '',
        items: Array.isArray(data.items) && data.items.length
          ? data.items.map((i) => ({
              item_id: i.item_id || '',
              requested_qty: Number(i.requested_qty || 0),
              remarks: i.remarks || ''
            }))
          : [{ ...emptyLine }]
      });
    } catch {
      setError('Network error while loading requisition for edit');
    }
  };

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px' };
  const btnBase = { padding: '0.5rem 0.85rem', border: 'none', borderRadius: '6px', cursor: 'pointer' };

  return (
    <div className="page-shell">
      <h2 style={{ color: '#2f855a' }}>Food Management - Purchase Requisition</h2>
      <div style={{ marginBottom: '0.7rem', color: '#4a5568' }}>
        Approval Mode: <strong>{settingsMode}</strong>
      </div>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar">
        <h3>{editingId ? 'Edit Requisition Draft' : 'Create Requisition Draft'}</h3>
        {!canCreatePR ? <div style={{ marginBottom: '0.6rem', color: '#7c2d12' }}>Maker access only: Purchase Staff can create/edit/submit PR.</div> : null}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem', marginBottom: '0.7rem' }}>
          <select value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} style={inputStyle}>
            <option value="">Select Store</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
          </select>
          <input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} style={inputStyle} placeholder="Remarks (optional)" disabled={!canCreatePR} />
        </div>
        <h4>Items</h4>
        {form.items.map((line, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '2fr 1fr 1fr 2fr auto', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <select value={line.item_id} onChange={(e) => setLine(idx, { item_id: e.target.value })} style={inputStyle} disabled={!canCreatePR}>
              <option value="">Select Item</option>
              {itemOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <input type="number" min="0.001" step="0.001" value={line.requested_qty} onChange={(e) => setLine(idx, { requested_qty: e.target.value })} style={inputStyle} placeholder="Qty" disabled={!canCreatePR} />
            <input
              value={line.item_id ? (itemById.get(String(line.item_id))?.unit || '') : ''}
              style={{ ...inputStyle, background: '#ecf0f1' }}
              placeholder="UOM"
              readOnly
            />
            <input value={line.remarks || ''} onChange={(e) => setLine(idx, { remarks: e.target.value })} style={inputStyle} placeholder="Line remarks" disabled={!canCreatePR} />
            <button onClick={() => removeLine(idx)} className="btn-danger" style={btnBase} disabled={!canCreatePR}>Remove</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem' }}>
          <button onClick={addLine} className="btn-primary" style={btnBase} disabled={!canCreatePR}>Add Line</button>
          <button onClick={saveDraft} className="btn-success" style={btnBase} disabled={!canCreatePR}>{editingId ? 'Update Draft' : 'Save Draft'}</button>
          <button onClick={resetForm} className="btn-muted" style={btnBase}>Reset</button>
        </div>
      </div>

      <div className="filter-bar">
        <h3>Requisition List</h3>
        {rows.length === 0 ? (
          <p>No requisitions found</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#2f855a', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>PR No</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Store</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Items</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Qty</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.6rem' }}>{r.requisition_no}</td>
                  <td style={{ padding: '0.6rem' }}>{r.store_name || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.item_count}</td>
                  <td style={{ padding: '0.6rem' }}>{Number(r.requested_total_qty || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.6rem', color: statusColor(r.status), fontWeight: 'bold' }}>{r.status}</td>
                  <td style={{ padding: '0.6rem', display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                    {(r.status === 'DRAFT' || r.status === 'REJECTED') && canCreatePR && (
                      <>
                        <button onClick={() => loadForEdit(r.id)} className="btn-primary" style={btnBase}>Edit</button>
                        <button onClick={() => submitReq(r.id)} className="btn-success" style={btnBase}>Submit</button>
                      </>
                    )}
                    {r.status === 'PENDING_APPROVAL' && canApprovePR && (
                      <>
                        <button onClick={() => approveReq(r.id)} className="btn-success" style={btnBase}>Approve</button>
                        <button onClick={() => rejectReq(r.id)} className="btn-danger" style={btnBase}>Reject</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default FoodPurchaseRequisition;
